import {
  STATEMENT_STATUS_LABEL,
  TICKET_STATUSES,
  laDoiTacDangTraTien,
  type PartnerChannelRow,
  type Statement,
  type StatementStatus,
  type PartnerTicket,
} from '@qlk/shared';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { coQuyen, useDuongDanTrang, usePhamViDuLieu, useToi } from '../../../lib/auth.js';
import {
  Avatar,
  DataTable,
  EmptyState,
  KhoiChuaCoKenh,
  KhoiMuc,
  Money,
  ngayNgan,
  Nut,
  PageHeader,
  StatStrip,
  StatusBadge,
  type CotBang,
  type OChiSo,
} from '../../../ui/index.js';
import {
  useDoiTac,
  useDoiTrangThaiTicket,
  useKenhTheoNetwork,
  useSaoKe,
  useTicket,
  useTienKy,
  useTomTatNetwork,
} from '../api.js';
import { useKyChung } from '../../../layout/kyChung.js';
import { TheDoiTac } from '../components/TheDoiTac.js';
import { tenKy } from '../../../lib/ky.js';

const TONE_SAO_KE: Record<StatementStatus, 'ok' | 'warn' | 'info' | 'neutral'> = {
  pending: 'warn',
  reconciled: 'info',
  confirmed: 'info',
  held: 'neutral',
  paid: 'ok',
};

export function TrangNetwork() {
  const duongDan = useDuongDanTrang('VẬN HÀNH');
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'networks', 'edit');

  /*
   * `GET /networks/summary` là **thẻ tổng của cả công ty** nên trả 403 cho
   * người bị giới hạn theo kênh được giao (rà soát bảo mật 16/10/2026, mục
   * V4a). Trang này không hiện khung đỏ cho nó, nhưng `latestPeriod` của nó là
   * **kỳ mặc định của cả trang** — mất kỳ là mọi bảng bên dưới đọc theo
   * `period` rỗng. Người bị giới hạn lấy kỳ hiện tại thay vì chờ một truy vấn
   * chắc chắn 403.
   */
  const { biGioiHanKenh, daBiet } = usePhamViDuLieu();
  const tomTat = useTomTatNetwork(daBiet && !biGioiHanKenh);

  /*
   * Kỳ của trang là **ô chọn kỳ chung** ở thanh trên (`?ky=`), giống Tổng
   * quan / Kênh / Hiệu suất. Trước 23/10/2026 trang này không có ô nào: kỳ
   * mặc định là "kỳ sao kê gần nhất" lấy từ `/networks/summary`, mà endpoint
   * đó trả 403 cho người bị giới hạn kênh — họ rơi vào kỳ rỗng và mọi nhãn ô
   * cụt mất phần sau dấu "·".
   */
  const kyChung = useKyChung();
  /* Kỳ ngoài mốc dữ liệu: dải chỉ số hiện `—` thay vì số 0 của API. */
  const kyRong = kyChung.kyRong;
  const kyTrang = kyChung.ky;
  const ky = kyTrang?.ma ?? '';
  const nhanKy = kyTrang?.nhan ?? '';
  const thangCuaKy = kyTrang?.thang ?? '';

  const doiTac = useDoiTac(thangCuaKy || undefined);
  const kenh = useKenhTheoNetwork(thangCuaKy || undefined);
  const saoKe = useSaoKe();
  const ticket = useTicket();
  const doiTrangThaiTicket = useDoiTrangThaiTicket();

  /* Tiền của đúng kỳ — ba ô chỉ số, thẻ đối tác và bảng kênh đọc chung. */
  const tien = useTienKy(ky, daBiet);

  const mcn = (doiTac.data ?? []).filter((d) => d.kind === 'mcn');
  const mcnCoKenh = mcn.filter((d) => d.channelCount > 0).length;
  // Cùng luật đếm với thẻ "Phần chia Network" ở màn Quản lý kênh (xem 12.0 — A3).
  const dangTraTien = (doiTac.data ?? []).filter(laDoiTacDangTraTien).length;
  const choDoiSoat = (saoKe.data ?? []).filter((s) => s.status === 'pending').length;

  /*
   * **Một** nguồn cho mọi con số tiền của trang: `GET /networks/tien-ky`.
   *
   * Nó tự chọn đường theo kỳ (xem `revenue.tienTheoKy`): kỳ trùng trọn một
   * tháng **đã chốt** thì đọc ảnh chụp lúc chốt và sao kê — khớp trang Đối
   * soát và **không** phải tạm tính; kỳ khác thì tính trên đúng khoảng ngày và
   * gắn "tạm tính". Phạm vi kênh áp ngay trong phép cộng ở máy chủ.
   */
  const tong = tien.data?.tong ?? null;
  const laTamTinh = tien.data !== undefined && !tien.data.daChot;
  const ghiChuTamTinh = laTamTinh ? 'Tạm tính — kỳ chưa chốt theo tháng' : undefined;

  /** Tiền theo từng đối tác / từng kênh của đúng kỳ này. */
  const tienDoiTac = useMemo(
    () => new Map((tien.data?.theoDoiTac ?? []).map((d) => [d.partnerId, d])),
    [tien.data],
  );
  const tienKenh = useMemo(
    () => new Map((tien.data?.theoKenh ?? []).map((d) => [d.channelId, d])),
    [tien.data],
  );

  const tyLeBinhQuan =
    tong && Number(tong.gross) > 0 ? (Number(tong.partnerShare) / Number(tong.gross)) * 100 : null;

  /*
   * Bốn thẻ chỉ số thành **một** `StatStrip`. Thẻ "Đối tác thanh toán" trước
   * đây là một ô nền đậm `bg-chu` tự vẽ — khác hẳn ba thẻ bên cạnh dù nói về
   * cùng một kỳ. Dải chỉ số không cho một ô nổi hơn ba ô kia, và đó là điều
   * đúng: số đối tác không quan trọng hơn số tiền.
   *
   * Ô "Đối tác thanh toán" đếm **đối tác**, không đếm hợp đồng: nhãn cũ ghi
   * "Hợp đồng Network" mà số lấy `activeContracts`, nên dòng mô tả chỏi với số.
   */
  const chiSo: OChiSo[] = [
    {
      nhan: 'Đối tác thanh toán · hiện tại',
      giaTri: doiTac.data?.length ?? 0,
      phu: `${String(mcnCoKenh)} đang có kênh · ${String(mcn.length - mcnCoKenh)} chưa gán kênh`,
      giaiThich: `Đếm đối tác, không đếm hợp đồng. Hiện có ${String(tomTat.data?.activeContracts ?? 0)} hợp đồng còn hiệu lực.`,
    },
    {
      nhan: `Doanh thu gộp · ${nhanKy}`,
      giaTri: <Money value={tong?.gross ?? null} />,
      phu: ghiChuTamTinh ?? `Qua ${String(dangTraTien)} network đang trả tiền`,
    },
    {
      nhan: `Phần chia Network · ${nhanKy}`,
      giaTri: <Money value={tong?.partnerShare ?? null} />,
      ...(tyLeBinhQuan === null
        ? ghiChuTamTinh === undefined
          ? {}
          : { phu: ghiChuTamTinh }
        : {
            phu:
              `Tỷ lệ bình quân ${tyLeBinhQuan.toFixed(1).replace('.', ',')}%` +
              (laTamTinh ? ' · tạm tính' : ''),
          }),
    },
    {
      /*
       * Người bị giới hạn kênh đang xem tổng của **kênh họ phụ trách**, không
       * phải của công ty — nhãn phải nói đúng số đang hiện.
       */
      nhan: `${biGioiHanKenh ? 'Kênh của bạn thực nhận' : 'Công ty thực nhận'} · ${nhanKy}`,
      giaTri: <Money value={tong?.thucNhan ?? null} />,
      phu: laTamTinh
        ? 'Tạm tính — sau thuế Mỹ, Network và VAT'
        : `Chờ đối soát ${String(choDoiSoat)} sao kê`,
    },
  ];

  /* --------------------------- Bảng kênh --------------------------- */

  /**
   * Số của một dòng kênh: sao kê nếu có, không thì **tạm tính** của module 08.
   *
   * Cột "Thực nhận" ở đây là thực nhận thật (sau thuế Mỹ, Network và VAT) khi
   * lấy số tạm tính; cột `net` của sao kê là phần sau Network theo file của
   * Network.
   */
  function soCuaKenh(d: PartnerChannelRow): {
    gross: string;
    partnerShare: string;
    thucNhan: string;
  } {
    const t = tienKenh.get(d.channelId);
    if (t) return { gross: t.gross, partnerShare: t.partnerShare, thucNhan: t.thucNhan };
    /* Kênh không có đồng nào trong kỳ: máy chủ không trả dòng nào cho nó. */
    return { gross: '0.00', partnerShare: '0.00', thucNhan: '0.00' };
  }

  const cotKenh: CotBang<PartnerChannelRow>[] = [
    {
      key: 'kenh',
      header: 'Kênh',
      width: '240px',
      render: (d) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={d.channelName} color={d.avatarColor} />
          <div className="min-w-0">
            <Link
              to={`/kenh/${d.channelId}`}
              className="block truncate font-semibold text-chu hover:text-nhan"
            >
              {d.channelName}
            </Link>
            <p className="truncate text-phu text-chu-mo">{d.channelHandle}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'network',
      header: 'Network',
      render: (d) => (
        <Link to={`/network/${d.partnerId}`} className="font-medium text-chu hover:text-nhan">
          {d.partnerName}
        </Link>
      ),
    },
    {
      key: 'tyLe',
      header: 'Tỷ lệ NW',
      align: 'right',
      render: (d) =>
        d.partnerKind === 'adsense_direct' ? (
          <span className="text-chu-mo">—</span>
        ) : (
          <span className="so-lieu">{d.splitLabel ?? '—'}</span>
        ),
    },
    {
      key: 'gop',
      header: 'Gộp',
      align: 'right',
      render: (d) => <Money value={soCuaKenh(d).gross} />,
    },
    {
      key: 'phanNw',
      header: 'Phần NW',
      align: 'right',
      render: (d) => <Money value={soCuaKenh(d).partnerShare} />,
    },
    {
      key: 'thucNhan',
      header: 'Thực nhận',
      align: 'right',
      render: (d) => <Money value={soCuaKenh(d).thucNhan} className="font-semibold text-ok-chu" />,
    },
    {
      key: 'hopDongDen',
      header: 'Hợp đồng đến',
      render: (d) =>
        d.contractEndDate ? (
          <span className="so-lieu text-chu-phu">{ngayNgan(d.contractEndDate)}</span>
        ) : (
          <span className="text-chu-mo">—</span>
        ),
    },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (d) =>
        d.partnerKind === 'adsense_direct' ? (
          <StatusBadge tone={d.linkStatus === 'linked' ? 'ok' : 'warn'}>
            {d.linkStatus === 'linked' ? 'Đã liên kết AdSense' : 'Chờ liên kết AdSense'}
          </StatusBadge>
        ) : Number(soCuaKenh(d).gross) > 0 ? (
          <StatusBadge tone="ok">Đang trả tiền</StatusBadge>
        ) : (
          <StatusBadge tone="info">Chờ kênh kiếm tiền</StatusBadge>
        ),
    },
  ];

  const tongKetKenh = {
    kenh: `${String(kenh.data?.length ?? 0)} kênh`,
    gop: <Money value={(kenh.data ?? []).reduce((t, d) => t + Number(soCuaKenh(d).gross), 0)} />,
    phanNw: (
      <Money value={(kenh.data ?? []).reduce((t, d) => t + Number(soCuaKenh(d).partnerShare), 0)} />
    ),
    thucNhan: (
      <Money value={(kenh.data ?? []).reduce((t, d) => t + Number(soCuaKenh(d).thucNhan), 0)} />
    ),
  };

  /* --------------------------- Bảng sao kê -------------------------- */

  const cotSaoKe: CotBang<Statement>[] = [
    {
      key: 'ky',
      header: 'Kỳ',
      render: (s) => <span className="so-lieu font-medium text-chu">{tenKy(s.period)}</span>,
    },
    { key: 'doiTac', header: 'Đối tác', render: (s) => s.partnerName },
    { key: 'gop', header: 'Gộp', align: 'right', render: (s) => <Money value={s.grossTotal} /> },
    {
      key: 'thucNhan',
      header: 'Thực nhận',
      align: 'right',
      render: (s) => <Money value={s.netTotal} />,
    },
    {
      key: 'dong',
      header: 'Dòng',
      align: 'right',
      render: (s) => (
        <span className="so-lieu">
          {s.lineCount}
          {s.unmatchedCount > 0 && (
            <span className="ml-1 text-bad-chu">({s.unmatchedCount} chưa khớp)</span>
          )}
        </span>
      ),
    },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (s) => (
        <StatusBadge tone={TONE_SAO_KE[s.status]}>{STATEMENT_STATUS_LABEL[s.status]}</StatusBadge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Network (MCN) & AdSense"
        phu={
          `${nhanKy}${kyTrang && kyTrang.nhanKhoang !== '' ? ` · ${kyTrang.nhanKhoang}` : ''} · ` +
          `${String(doiTac.data?.length ?? 0)} đối tác thanh toán · ` +
          `${String(tomTat.data?.activeContracts ?? 0)} hợp đồng còn hiệu lực`
        }
        giaiThich={
          `${String(mcn.length)} Network và ` +
          `${String((doiTac.data ?? []).length - mcn.length)} tài khoản AdSense trực tiếp. ` +
          'Tiền của kỳ lấy từ sao kê khi đã có, còn lại là số tạm tính của module Doanh thu.'
        }
        actions={
          <>
            {coSua && (
              <>
                <Link
                  to="/network/sao-ke/moi"
                  className="vung-bam inline-flex items-center justify-center rounded-[10px] border border-vien bg-be-mat px-4 text-co-ban font-semibold text-chu hover:bg-neutral-nen"
                >
                  Tải sao kê
                </Link>
                <Link
                  to="/network/moi"
                  className="vung-bam inline-flex items-center justify-center rounded-[10px] bg-nhan px-4 text-co-ban font-semibold text-white hover:bg-nhan-dam"
                >
                  + Thêm hợp đồng Network
                </Link>
              </>
            )}
          </>
        }
      />

      <KhoiChuaCoKenh tenTrang="Network (MCN)" />

      <div className="mb-5">
        <StatStrip
          aria-label={`Chỉ số ${nhanKy}`}
          o={chiSo}
          /* Kỳ ngoài mốc dữ liệu (link cũ `?ky=2017`): API trả 0, mà `0,00 $` đọc
           ra là "tụt về không". Hiện `—`; dòng "Không có số liệu trong kỳ này"
           nằm cạnh ô chọn kỳ (`ui/ChonKy`). */
          kyRong={kyRong}
        />
      </div>

      {/* Lưới thẻ đối tác */}
      <div className="mb-5">
        <KhoiMuc tieuDe="Đối tác thanh toán" moTa="Bấm vào thẻ để xem hợp đồng, kênh và sao kê">
          {doiTac.error ? (
            <div className="rounded-the border border-vien bg-be-mat">
              <EmptyState
                loi
                title="Không tải được danh sách đối tác"
                description={doiTac.error.message}
                action={
                  <Nut
                    onClick={() => {
                      void doiTac.refetch();
                    }}
                  >
                    Thử lại
                  </Nut>
                }
              />
            </div>
          ) : doiTac.isPending ? (
            <div className="grid grid-cols-1 gap-4 @min-[600px]/noi-dung:grid-cols-2 @min-[900px]/noi-dung:grid-cols-3 @min-[1250px]/noi-dung:grid-cols-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`khung-${String(i)}`}
                  className="h-[188px] animate-pulse rounded-the border border-vien bg-be-mat"
                />
              ))}
            </div>
          ) : doiTac.data.length === 0 ? (
            <div className="rounded-the border border-vien bg-be-mat">
              <EmptyState
                title="Chưa có đối tác thanh toán nào"
                /* Chỉ mời thêm đối tác khi người đang xem thật sự có nút đó. */
                description={
                  coSua
                    ? 'Dùng nút “+ Thêm hợp đồng Network” ở trên để bắt đầu theo dõi dòng tiền.'
                    : 'Chưa khai đối tác nào cho các kênh bạn được xem.'
                }
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 @min-[600px]/noi-dung:grid-cols-2 @min-[900px]/noi-dung:grid-cols-3 @min-[1250px]/noi-dung:grid-cols-6">
              {doiTac.data.map((d) => (
                <TheDoiTac
                  key={d.id}
                  doiTac={d}
                  nhanKy={`Gộp · ${nhanKy}`}
                  gross={tienDoiTac.get(d.id)?.gross ?? null}
                  tamTinh={laTamTinh}
                />
              ))}
            </div>
          )}
        </KhoiMuc>
      </div>

      {/* Kênh theo Network */}
      <div className="mb-5">
        <KhoiMuc
          tieuDe={`Kênh theo Network · ${nhanKy}`}
          moTa="Tỷ lệ chia và kỳ thanh toán lấy từ từng hợp đồng"
          satLe
        >
          {kenh.error ? (
            <EmptyState
              loi
              title="Không tải được danh sách kênh"
              description={kenh.error.message}
            />
          ) : (
            <DataTable
              caption="Kênh đang gán cho từng Network"
              columns={cotKenh}
              rows={kenh.data ?? []}
              keyOf={(d) => d.linkId}
              dangTai={kenh.isPending}
              tongKet={tongKetKenh}
              khiRong={
                <EmptyState
                  title="Chưa gán kênh nào cho Network"
                  description="Mở một đối tác rồi chọn Gán kênh."
                />
              }
            />
          )}
        </KhoiMuc>
      </div>

      <div className="grid grid-cols-1 gap-5 @min-[900px]/noi-dung:grid-cols-2">
        {/* Sao kê theo kỳ */}
        <KhoiMuc tieuDe="Sao kê theo kỳ" satLe>
          {saoKe.error ? (
            <EmptyState loi title="Không tải được sao kê" description={saoKe.error.message} />
          ) : (
            <DataTable
              caption="Sao kê Network theo kỳ"
              columns={cotSaoKe}
              rows={saoKe.data ?? []}
              keyOf={(s) => s.id}
              dangTai={saoKe.isPending}
              khiRong={
                <EmptyState
                  title="Chưa có sao kê nào"
                  description={
                    coSua
                      ? 'Dùng nút Tải sao kê để import file của Network.'
                      : 'Chưa có sao kê nào cho các đối tác bạn được xem.'
                  }
                />
              }
            />
          )}
        </KhoiMuc>

        {/* Yêu cầu hỗ trợ */}
        <KhoiMuc tieuDe="Yêu cầu hỗ trợ gửi Network">
          {ticket.isPending ? (
            <p className="text-co-ban text-chu-phu">Đang tải…</p>
          ) : (ticket.data ?? []).length === 0 ? (
            <EmptyState
              title="Chưa có yêu cầu nào"
              description="Khi cần Network hỗ trợ, tạo yêu cầu ở trang chi tiết đối tác."
            />
          ) : (
            <ul className="flex flex-col gap-3">
              {(ticket.data ?? []).map((t) => (
                <DongTicket
                  key={t.id}
                  ticket={t}
                  coSua={coSua}
                  dangChay={doiTrangThaiTicket.isPending}
                  onDoiTrangThai={(status) => {
                    doiTrangThaiTicket.mutate({ id: t.id, status });
                  }}
                />
              ))}
            </ul>
          )}
        </KhoiMuc>
      </div>
    </>
  );
}

const TONE_TICKET = { open: 'warn', in_progress: 'info', done: 'ok' } as const;
const NHAN_TICKET = { open: 'Đang mở', in_progress: 'Đang xử lý', done: 'Đã xong' } as const;

function DongTicket({
  ticket,
  coSua,
  dangChay,
  onDoiTrangThai,
}: {
  ticket: PartnerTicket;
  coSua: boolean;
  dangChay: boolean;
  onDoiTrangThai: (status: PartnerTicket['status']) => void;
}) {
  return (
    <li className="rounded-[12px] border border-vien p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-chu">{ticket.subject}</p>
          <p className="text-phu text-chu-mo">
            {ticket.partnerName} · {ngayNgan(ticket.createdAt)}
          </p>
        </div>
        <StatusBadge tone={TONE_TICKET[ticket.status]}>{NHAN_TICKET[ticket.status]}</StatusBadge>
      </div>
      <p className="mt-2 text-co-ban text-chu-phu">{ticket.body}</p>
      {coSua && (
        <div className="mt-3 flex flex-wrap gap-2">
          {TICKET_STATUSES.filter((s) => s !== ticket.status).map((s) => (
            <Nut
              key={s}
              kieu="nhe"
              dangChay={dangChay}
              onClick={() => {
                onDoiTrangThai(s);
              }}
            >
              Chuyển sang “{NHAN_TICKET[s]}”
            </Nut>
          ))}
        </div>
      )}
    </li>
  );
}
